import { useMemo } from "react";
import { useIntl } from "react-intl";
import { useWatch } from "react-hook-form";

import { useGetFreightModesConfig } from "api/queries/location/useGetFreightModesConfig";
import type { InvitedModes } from "api/models/draftRequest.model";
import {
  countSelectedFreightModesByCategory,
  formatFreightModeCategorySummary,
  resolveSelectedFreightModes,
} from "@RequestFormV2/utils/freightModeUtils";

export function useFreightModeHeaderSummary({ enabled = true }: { enabled?: boolean } = {}) {
  const intl = useIntl();
  const location = useWatch({ name: "organizational.location" }) as string | undefined;
  const invitedModes = useWatch({ name: "invited_modes" }) as InvitedModes | undefined;

  const { data: freightModesConfig } = useGetFreightModesConfig(location ?? "", {
    enabled: enabled && !!location,
  });

  return useMemo(() => {
    const availableModes = freightModesConfig?.freight_modes?.available_modes ?? [];
    const selectedModes = resolveSelectedFreightModes(invitedModes, availableModes);
    const selectedCount = selectedModes.length;

    if (selectedCount === 0) {
      return { label: "-" };
    }

    if (selectedCount === 1) {
      return { label: selectedModes[0].label };
    }

    const counts = countSelectedFreightModesByCategory(selectedModes);
    return { label: formatFreightModeCategorySummary(counts, intl) };
  }, [freightModesConfig, invitedModes, intl]);
}
